import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Users, BookOpen, FileText, TrendingUp, Handshake, ShieldCheck, Award, Euro, Package, Tag, Settings, Mail, Star, BarChart3, ShieldAlert } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-dashboard-page">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-8 h-8 text-brand-green" />
        <p className="label">Admin</p>
      </div>
      <h1 className="font-heading text-4xl font-black mb-2">Tableau de bord</h1>
      <p className="text-gray-600 mb-8">Hello {user?.name}, voici un aperçu de la plateforme SF Learning.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Euro} label="Revenu total" value={`${(stats.revenue_eur || 0).toFixed(0)}€`} accent="text-brand-green" />
        <Stat icon={Award} label="Ventes" value={stats.sales || 0} />
        <Stat icon={Users} label="Apprenants" value={stats.users || 0} />
        <Stat icon={Handshake} label="Affiliés" value={stats.affiliates || 0} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <ModuleCard to="/admin/courses" icon={BookOpen} title="Formations" desc="Crée, édite et organise tes formations." />
        <ModuleCard to="/admin/stats" icon={BarChart3} title="Statistiques" desc="Analyse avancée des ventes, formations, avis et engagement." />
        <ModuleCard to="/admin/team" icon={Users} title="Équipe" desc="Ajoute coachs, support client et accès internes." />
        <ModuleCard to="/admin/system" icon={ShieldAlert} title="Logs & Santé" desc="Surveille erreurs, actions sensibles et statut système." badge={stats.open_system_logs} />
        <ModuleCard to="/admin/students" icon={Users} title="Apprenants" desc="Ajoute, suis et supprime tes apprenants." />
        <ModuleCard to="/admin/packs" icon={Package} title="Packs" desc="Vends plusieurs formations groupées." />
        <ModuleCard to="/admin/promo" icon={Tag} title="Codes promo" desc="Lance des campagnes (Black Friday, lancement...)" />
        <ModuleCard to="/admin/blog" icon={FileText} title="Blog" desc="Publie des articles SEO pour attirer du trafic." />
        <ModuleCard to="/admin/settings" icon={Settings} title="Textes du site" desc="Édite les textes de la page d'accueil, footer, etc." />
        <ModuleCard to="/admin/emails" icon={Mail} title="Emails" desc="Personnalise les emails envoyés aux apprenants." />
        <ModuleCard to="/admin/testimonials" icon={Star} title="Avis apprenants" desc={`${stats.pending_testimonials || 0} en attente`} badge={stats.pending_testimonials} />
        <ModuleCard to="/admin/assignments" icon={FileText} title="Devoirs à corriger" desc={`${stats.pending_assignments || 0} en attente`} badge={stats.pending_assignments} />
        <ModuleCard to="/admin/affiliates" icon={Handshake} title="Affiliés" desc="Suivi des parrains et commissions." />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent = "" }) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 text-gray-500 mb-1">
        <Icon className="w-4 h-4" />
        <p className="label">{label}</p>
      </div>
      <p className={`font-heading text-3xl font-black ${accent}`}>{value}</p>
    </div>
  );
}

function ModuleCard({ to, icon: Icon, title, desc, accent, badge }) {
  const count = Number(badge) || 0;
  const hasBadge = count > 0;
  return (
    <Link to={to} className={`relative card p-6 hover:shadow-elev transition-shadow group ${accent || hasBadge ? "ring-2 ring-brand-orange" : ""}`} data-testid={`admin-module-${title.toLowerCase()}`}>
      {hasBadge && (
        <span
          className="absolute top-3 right-3 min-w-[1.5rem] h-6 px-2 inline-flex items-center justify-center rounded-full bg-brand-orange text-white text-xs font-bold shadow"
          title={`${count} élément(s) à traiter`}
          data-testid={`admin-badge-${title.toLowerCase()}`}
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
      <Icon className="w-8 h-8 text-brand-green mb-3" />
      <h3 className="font-heading text-xl font-bold group-hover:text-brand-green">{title}</h3>
      <p className="text-sm text-gray-600 mt-1">{desc}</p>
    </Link>
  );
}
